import { useEffect, useRef, useMemo } from 'react';
import DocumentTitle from 'react-document-title';
import { Navigate, useNavigate } from 'react-router-dom';

import { Box } from '@hivebrite/design-system/Box';
import { Container } from '@hivebrite/design-system/Container';
import { Grid } from '@hivebrite/design-system/Grid';
import { Loader } from '@hivebrite/design-system/Loader';
import { SimpleCard } from '@hivebrite/design-system/SimpleCard';

import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import EventCard from 'hb-react/shared/apps/Events/components/EventsIndexContainer/components/EventCard';
import EventCardLoading from 'hb-react/shared/apps/Events/components/EventsIndexContainer/components/EventCardLoading';
import EventsCards from 'hb-react/shared/apps/Events/components/EventsIndexContainer/components/EventsCards';
import {
  colWidthBreakpoints,
  perPageBreakpoints,
} from 'hb-react/shared/apps/Events/components/EventsIndexContainer/utils';
import { EventsIndexContextProvider } from 'hb-react/shared/apps/Events/contexts/EventsIndexContext';
import { useEvent } from 'hb-react/shared/apps/Events/hooks/useEvent';
import { getZoomCompletedTitle } from 'hb-react/shared/apps/Events/pages/ZoomCompleted/utils/getZoomCompletedTitle';
import { getEventDedicatedPath } from 'hb-react/shared/apps/Events/utils/getEventDedicatedUrl';
import useTimeZoneConfiguration from 'hb-react/shared/hooks/useTimeZoneConfiguration';
import dayjs from 'hb-react/shared/utils/dayjs/dayjs';
import { generatePageTitle } from 'hb-react/shared/utils/generatePageTitle';
import { getPageTitleFromNetwork } from 'hb-react/shared/utils/getPageTitleFromNetwork';

export const ZoomCompletedPage = ({
  config,
}: {
  config: {
    routes: { events_path: (params: any) => string };
  };
}) => {
  const { currentNetwork, currentUser } = useDefaultAppContexts();
  const { topicId, whitelabelURL } = useTopicContext();
  const { event, isLoading: eventIsLoading } = useEvent();
  const navigate = useNavigate();
  const timezoneConfiguration = useTimeZoneConfiguration();

  const now = useMemo(() => dayjs().valueOf(), []);

  const meetingEndReason = useRef(
    window.sessionStorage.getItem('zoomMeetingEndReason'),
  );

  useEffect(() => {
    window.sessionStorage.removeItem('zoomMeetingEndReason');
  }, []);

  // Navigate away only if meetingEndReason is undefined
  if (!meetingEndReason.current) {
    return <Navigate to=".." />;
  }

  if (eventIsLoading) {
    return <Loader />;
  }

  if (!event) {
    return <Navigate to=".." />;
  }

  return (
    <EventsIndexContextProvider
      isMapDisplayed={false}
      onEventCardClick={(event) => {
        navigate(
          getEventDedicatedPath({
            hasUser: currentUser,
            event,
            topicId,
            isWhitelabelGroup: !!whitelabelURL,
          }),
        );
      }}
      eventBeingDeleted={null}
      hiddenUsersIds={[]}
      setHiddenUsersIds={() => {}}
      refreshMapMarkersRef={() => {}}
      subscribeToEventDeletedHandlers={() => {}}
      unsubscribeFromEventDeletedHandlers={() => {}}
      selectedEvent={null}
      setSelectedEvent={() => {}}
    >
      <DocumentTitle
        title={generatePageTitle({
          networkName: getPageTitleFromNetwork(currentNetwork),
          title: event?.title,
        })}
      />
      <Container>
        <Box
          flexDirection="column"
          gridRowGap={8}
          mt={6}
          width="100%"
          alignItems="center"
        >
          <SimpleCard
            cardProps={{
              width: '100%',
            }}
            title={getZoomCompletedTitle(meetingEndReason.current, event)}
            description={s__('Events|Thank you for attending!')}
            faIconProps={{
              iconName: 'calendar-day',
            }}
          />
          <Box display="block" width="100%">
            <Grid mx={0} mt={-2} mb={0}>
              <EventsCards
                index={0}
                name={s__('Events|Discover upcoming events')}
                noResultsMessage={__('No upcoming events found')}
                resultsFoundMessage={(count) =>
                  n__(
                    '%{count} upcoming event found',
                    '%{count} upcoming events found',
                    count,
                  ).i__({ count })
                }
                params={{
                  include_network_events: true,
                  query: {
                    pinned_on_index_page: false,
                    gte_start_date: now,
                    order: 'asc',
                  },
                }}
                singleLine
                showIfEmpty={false}
                prefetchPages={5}
                onClearFilters={() => {}}
                key="upcoming"
                colWidthBreakpoints={colWidthBreakpoints({
                  isMapDisplayed: false,
                })}
                perPageBreakpoints={perPageBreakpoints({
                  isMapDisplayed: false,
                  singleLine: true,
                })}
                maxPerPage={4}
                fetchPath={config.routes.events_path}
                ItemComponent={(item) => (
                  <EventCard
                    {...item}
                    timezoneConfiguration={timezoneConfiguration}
                  />
                )}
                ItemLoadingComponent={EventCardLoading}
              />
            </Grid>
          </Box>
        </Box>
      </Container>
    </EventsIndexContextProvider>
  );
};
